import type { NurtureHoldReason } from "./nurtureHold";
import { isWindowGated, type Channel, type Outcome } from "./types";
import { windowState } from "./window";

/**
 * The Campaigns agent's decision function (Agent Brains section 5.11).
 *
 * Section 5.11's charter, verbatim: mission "One-to-many revenue waves, on a
 * calendar and on triggers."; never "Never messages a contact in an active
 * conversation. Never bypasses opt-in."; handoff "Takes personas from
 * Analysis. Yields to Conversations on live threads."; produces "Campaign
 * waves and their measured ROI."
 *
 * WHAT THIS FILE DOES NOT DO. The charter says a wave "takes personas from
 * Analysis," and no persona or segmentation model exists anywhere in this
 * codebase: there is no labelled behavioural clustering, no Analysis agent
 * output, nothing to take. Inventing one here would be inventing spec
 * (CLAUDE.md section 12), so `CampaignAudienceCriteria` below is deliberately
 * NOT a persona. It is a small set of explicit, honestly-named predicates over
 * real columns (`threads.channel`, `outcomes.label`), authored by a human per
 * campaign, the same "owner-ratified provisional" register F-15 already uses
 * for a pattern with no statistical backing yet. The day a real persona model
 * exists, it plugs in here as a wider criteria shape; this file's job is only
 * ever to enforce the two "never" rules against whatever criteria it is given.
 *
 * This file is the decision half only, exactly the split `decideReviewAsk`
 * (reviewAsk.ts) and `planFollowUp` (followUp.ts) already establish: given one
 * campaign definition and what is known about one candidate contact, decide
 * whether sending this wave to them right now is safe. It never composes
 * anything, never queries a database, never sends anything. The machinery
 * that finds candidates, drives an eligible send through the real send path,
 * and measures revenue attribution afterward sits in
 * `packages/agents/src/campaignSweep.ts` and `packages/core/src/campaignRoi.ts`.
 *
 * "ACTIVE CONVERSATION", DEFINED ONCE. Section 7 says a Shepherd nudge, a
 * Campaign wave, a live Reply thread and a review request all compete for the
 * same customer through the one arbiter, and the charter's own "never" line
 * singles out active conversations as the one thing a wave must never
 * interrupt. Rather than inventing a second definition of "still live" next to
 * `planFollowUp`'s, this file reuses the same primitives `followUp.ts` reads
 * (`isFrozen`, `hasPendingApprovalDraft`, "who spoke last") plus the window
 * state machine directly (`windowState`, `isWindowGated`), because a thread
 * still inside its 24h Meta window is a live conversation happening right now
 * by definition, whether or not it has gone quiet by follow-up's own
 * three-day standard. Section 0: when genuinely unsure whether a contact is
 * "active," exclude them. Every branch below that could go either way,
 * including the very first one to check, resolves toward NOT sending.
 */

/**
 * A campaign's targeting, authored by a human per campaign and stored
 * verbatim on the `campaigns` row (migration 0018). Single-channel by
 * construction: a wave message is written for one platform's mechanics and
 * register, and `threads_client_contact_channel_uq` already means one
 * contact has at most one thread per channel, so a channel plus an optional
 * outcome-label filter resolves to exactly one addressable thread per
 * matching contact, never an ambiguous choice between several.
 *
 * DELIBERATELY MINIMAL. Two predicates, not a rules engine: recency windows,
 * spend tiers, and anything richer belong to a campaign-authoring surface
 * this task does not build (out of scope, logged in FINDINGS.md TA-183).
 * Adding knobs nobody has a UI to set is inventing capability nobody asked
 * for, the opposite of section 12's "do not silently invent spec."
 */
export interface CampaignAudienceCriteria {
  readonly channel: Channel;
  /**
   * Only contacts whose most recent unreversed outcome on the target thread
   * carries one of these labels. Omit to target every contact on the channel
   * regardless of outcome history (a genuinely cold or full-list wave).
   */
  readonly outcomeLabels?: readonly Outcome[];
}

/** Human-authored input to a wave. Stored on the `campaigns` table. */
export interface CampaignDefinition {
  readonly id: string;
  readonly name: string;
  readonly audienceCriteria: CampaignAudienceCriteria;
  /** Never composed at fire time. See `campaignSweep.ts`'s refusal on
   * `templateApproved === false` for why this can never quietly ship. */
  readonly messageTemplate: string;
  readonly templateApproved: boolean;
}

export type CampaignWaveAction =
  | "eligible"
  | "excluded_unapproved_template"
  | "excluded_suppressed"
  | "excluded_erasure_pending"
  | "excluded_active_conversation"
  | "excluded_open_complaint"
  | "excluded_no_optin"
  | "excluded_already_sent_this_wave"
  | "excluded_wave_cap"
  | "excluded_not_in_audience";

/**
 * V5 CAMPAIGN (open question 7): the most campaign waves one contact is sent
 * in any 30 days, across every campaign. AWAITING RATIFICATION (T3: a
 * frequency cap is Kabir's number, never auto-tuned). It sits on top of the
 * arbiter's daily cap (section 7, enforced globally) and never replaces it.
 */
export const CAMPAIGN_WAVES_PER_CONTACT_30D = 2; // AWAITING RATIFICATION

export interface CampaignWaveDecision {
  readonly action: CampaignWaveAction;
  /** One line a human or a log line can read without more context. */
  readonly reason: string;
}

export interface CampaignCandidateInput {
  readonly channel: Channel;
  /** The candidate's most recent unreversed outcome on the target thread, or
   * null. Kept as `Outcome | string` like `FollowUpInput.outcome`: forward
   * compatible with a label this enum has not been taught yet, failing
   * closed into "does not match" rather than throwing. */
  readonly outcome: Outcome | string | null;
  /** Global suppression list (section 7): bereavement, an opt-out, a
   * sensitive life change. Beats every priority, including a wave. */
  readonly isSuppressed: boolean;
  /** A6.3, TA-588: a deletion request for this contact is still pending. */
  readonly erasurePending: boolean;
  /** An open row in `thread_freezes` (the injection freeze). T3, human-cleared only. */
  readonly isFrozen: boolean;
  /** A `pending_approval` draft sits on this thread: a human is answering
   * this contact right now. */
  readonly hasPendingApprovalDraft: boolean;
  readonly lastCustomerMessageAt: Date | null;
  readonly lastOutboundAt: Date | null;
  /** Marketing Messages opt-in captured for this contact (`threads.has_marketing_optin`). */
  readonly hasMarketingOptIn: boolean;
  /** A row already exists in `campaign_sends` for this contact and THIS
   * campaign specifically. Never re-send the same wave to the same contact. */
  readonly alreadySentThisWave: boolean;
  /**
   * V5: how many `campaign_sends` rows this contact has in the 30 days before
   * `now`, over every campaign. A count that is not a whole number of at least
   * zero is unreadable and holds the send (section 0).
   */
  readonly wavesLast30d: number;
  /**
   * TA-1361 (CLAUDE.md section 3, B13): the thread's latest route is class D,
   * or the contact holds an open complaint (`nurtureHoldFor`, nurtureHold.ts),
   * read by the caller. Null when neither holds. Required, so a caller cannot
   * forget it. TA-1371 C4: or a person's takeover, read only while
   * `ACKRA_ENABLE_TAKEOVER_HOLD` is on.
   */
  readonly nurtureHold: NurtureHoldReason | null;
  readonly now: Date;
}

function plan(action: CampaignWaveAction, reason: string): CampaignWaveDecision {
  return { action, reason };
}

/** The log sentence for each arm of the nurture hold. A Record, so a new arm fails the compile until it has one. */
const NURTURE_HOLD_WAVE_REASON: Readonly<Record<NurtureHoldReason, string>> = {
  open_complaint: "the contact holds an open complaint; a person answers it, never a wave (TA-1361)",
  class_d_thread: "the thread's latest route is class D; a person handles it, never a wave (TA-1361, section 3)",
  human_takeover: "a person on the shop's team took this contact recently; a wave waits until the hold lifts (TA-1371 C4)",
};

function matchesAudience(
  criteria: CampaignAudienceCriteria,
  candidate: CampaignCandidateInput,
): boolean {
  if (candidate.channel !== criteria.channel) return false;
  if (criteria.outcomeLabels !== undefined) {
    if (candidate.outcome === null) return false;
    if (!(criteria.outcomeLabels as readonly string[]).includes(candidate.outcome)) return false;
  }
  return true;
}

/**
 * Whether this contact is, right now, in a conversation a wave must not
 * interrupt. Four independent ways in, any one of them sufficient, mirroring
 * `planFollowUp`'s own reasoning for "still live":
 *
 * 1. Frozen (a quarantined thread is treated as active, conservatively).
 * 2. A pending human draft (someone is answering this contact right now).
 * 3. Still inside the Meta 24h window on a window-gated channel: a live
 *    conversation is happening whether or not it has gone quiet by
 *    follow-up's own, longer, three-day standard.
 * 4. The customer spoke last, or spoke at all and never got a reply: we owe
 *    them, and a wave is never the answer to that debt (`planFollowUp`'s
 *    `customer_spoke_last`, tie goes to the customer).
 */
function isActiveConversation(c: CampaignCandidateInput): boolean {
  if (c.isFrozen) return true;
  if (c.hasPendingApprovalDraft) return true;

  if (isWindowGated(c.channel)) {
    const state = windowState({ lastCustomerMessageAt: c.lastCustomerMessageAt, now: c.now });
    if (state !== "closed") return true;
  }

  if (c.lastCustomerMessageAt !== null) {
    if (c.lastOutboundAt === null) return true;
    if (c.lastCustomerMessageAt.getTime() >= c.lastOutboundAt.getTime()) return true;
  }

  return false;
}

/**
 * Section 0: precision beats recall. The only way out of this function with
 * `eligible` is clearing every check in order; any one of them being true is
 * enough to withhold. Order matters and is deliberate:
 *
 * 1. `excluded_unapproved_template`: a campaign-level gate, checked before any
 *    per-contact fact, so an unreviewed template can never ship because a
 *    single candidate happened to fail some other check first.
 * 2. `excluded_suppressed`: section 7, "enforced globally... beats every
 *    priority."
 * 2a. `excluded_erasure_pending`: A6.3, TA-588, a deletion request is pending.
 * 3. `excluded_active_conversation`: the charter's first "never".
 * 3a. `excluded_open_complaint` (TA-1361): a class D thread or an open
 *    complaint (`nurtureHoldFor`). A person's, never a wave's (CLAUDE.md
 *    section 3, B13). Named ahead of opt-in, the cap and the audience, so a
 *    held contact is recorded as held, not as merely out of reach. Since
 *    TA-1371 C4 it also carries the third arm, a person's takeover (built
 *    switched off): the action keeps its TA-1361 name, and the restraint row
 *    carries the hold's own reason, so the row says which arm held.
 * 4. `excluded_no_optin`: the charter's second "never", checked
 *    UNCONDITIONALLY rather than deferred to the send path's own window gate.
 *    `evaluateWindowGate` alone would permit a marketing-flavoured message
 *    through an incidentally still-open window with no real opt-in on file
 *    (section 8: "inside the window... promotional content is permitted
 *    without a separate opt-in"), which is true and correct for a live
 *    conversation, but a segment wave is a marketing broadcast by definition
 *    and "never bypasses opt-in" cannot be a matter of the window happening
 *    to still be open from unrelated recent contact. This function therefore
 *    requires the real opt-in every time, never relying on that coincidence.
 * 5. `excluded_already_sent_this_wave`: idempotency, one send per contact per
 *    wave.
 * 5a. `excluded_wave_cap` (V5): the per-contact frequency cap, after the
 *    facts that name this contact and before the audience match.
 * 6. `excluded_not_in_audience`: the actual targeting match. Checked LAST, not
 *    first, on the same "SQL can only over-select, TS decides exactly"
 *    discipline `reviewSweep.ts` documents for its own eligibility check: the
 *    machinery's SQL prefilter is deliberately loose, and this is the one
 *    place the real audience criteria are applied.
 */
export function decideCampaignWave(
  definition: Pick<CampaignDefinition, "templateApproved" | "audienceCriteria">,
  candidate: CampaignCandidateInput,
): CampaignWaveDecision {
  if (!definition.templateApproved) {
    return plan(
      "excluded_unapproved_template",
      "the campaign's message template has not been approved; refusing every send until it is",
    );
  }

  if (candidate.isSuppressed) {
    return plan(
      "excluded_suppressed",
      "the contact is on the global suppression list (section 7)",
    );
  }

  if (candidate.erasurePending) {
    return plan(
      "excluded_erasure_pending",
      "a deletion request for the contact is pending (A6.3); nothing is sent to them",
    );
  }

  if (isActiveConversation(candidate)) {
    return plan(
      "excluded_active_conversation",
      "the contact is in an active conversation; a segment wave never interrupts one (charter's first never)",
    );
  }

  if (candidate.nurtureHold !== null) {
    return plan("excluded_open_complaint", NURTURE_HOLD_WAVE_REASON[candidate.nurtureHold]);
  }

  if (!candidate.hasMarketingOptIn) {
    return plan(
      "excluded_no_optin",
      "the contact has not given Marketing Messages opt-in; a segment wave never sends without it (charter's second never)",
    );
  }

  if (candidate.alreadySentThisWave) {
    return plan(
      "excluded_already_sent_this_wave",
      "this contact already received this exact campaign wave",
    );
  }

  if (
    !Number.isInteger(candidate.wavesLast30d) ||
    candidate.wavesLast30d < 0 ||
    candidate.wavesLast30d >= CAMPAIGN_WAVES_PER_CONTACT_30D
  ) {
    return plan(
      "excluded_wave_cap",
      "the contact already received the most campaign waves allowed in 30 days (or the count is unreadable); one attentive shop, never a stream",
    );
  }

  if (!matchesAudience(definition.audienceCriteria, candidate)) {
    return plan(
      "excluded_not_in_audience",
      "the candidate does not match this campaign's audience criteria",
    );
  }

  return plan(
    "eligible",
    "approved template, not suppressed, not in an active conversation, opted in, not already sent, matches the audience",
  );
}
